import * as Sentry from "@sentry/react";

import { WELL_LAYER, WELL_SOURCE_LAYER } from "constants/mapLayers.constants";

import { getMapInstance } from "components/map/utils/mapInstanceRegistry";

/**
 * The multilateral legs belonging to `parentUwis`, read off the well layer the
 * map is rendering.
 *
 * A leg is not a well any of the well endpoints will answer for: it has its own
 * id, its own survey and its own rectangle in a cross-section, but it reaches
 * the browser only as a feature of its parent on the map tile (`group === "leg"`,
 * carrying `legUwi`). `mapboxHelper.ts` reads them exactly this way when the map
 * builds a selection, which is why the dashboard's sections have them and a
 * caller that assembles its own well list does not.
 *
 * A leg follows its parent: one whose parent is not in `parentUwis` is dropped
 * rather than carried on its own, the same rule `buildWellsForScene` applies to
 * subordinate wells. The parent list is therefore expected to be filter-scoped
 * already — a leg cannot be scoped on its own, because the filter service does
 * not know it either.
 *
 * With no map on the dashboard there is nothing to read and the answer is empty.
 * That is the state the caller was already in, not a failure.
 *
 * The map is read where it stands: `querySourceFeatures` answers from the tiles
 * that are loaded right now, so a section derived while the wells are off screen
 * can come back short of legs. The derive runs again whenever the request is
 * rebuilt, which is what puts them back, and that is the accepted behaviour -
 * the alternative is asking the tile server for wells the map is not showing.
 */
export function legUwisForWells(parentUwis: readonly string[]): string[] {
  const map = getMapInstance();
  if (!map || parentUwis.length === 0) {
    return [];
  }
  // The style can be mid-reload (a basemap switch re-adds every layer), and
  // querying a source that is not there throws.
  if (!map.getLayer?.(WELL_LAYER)) {
    return [];
  }

  const parents = new Set(parentUwis);
  let features;
  try {
    features = map.querySourceFeatures(WELL_LAYER, {
      sourceLayer: WELL_SOURCE_LAYER,
      filter: ["in", ["get", "Uwi"], ["literal", [...parents]]]
    });
  } catch (err) {
    // Nothing to add to the section — the caller's list stands as it is. The
    // section is still drawn, so nothing on screen says the legs are missing;
    // reported rather than swallowed, or the only trace would be a short
    // section the user has no reason to question.
    Sentry.captureException(err);
    return [];
  }

  const legUwis = new Set<string>();
  for (const feature of features ?? []) {
    const properties = feature?.properties;
    if (properties?.group !== "leg") continue;
    // Tile properties are untyped, so the string is checked rather than
    // asserted: a number there would be added to the section as one.
    const legUwi = typeof properties.legUwi === "string" ? properties.legUwi : "";
    // The parent check is the rule, not the tile filter: one feature per leg is
    // emitted per tile the leg crosses, so the same leg arrives many times.
    if (!legUwi || !parents.has(properties.Uwi)) continue;
    legUwis.add(legUwi);
  }

  return [...legUwis];
}
